import {
    Box,
    Card,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core'
import {
    IconUsers,
    IconSalad,
    IconBottleFilled,
    IconLeaf,
    IconPlant2,
    IconHammer,
    IconPlaneDeparture,
    IconTruckDelivery,
    IconShoppingCart,
} from '@tabler/icons-react'
import type { FC } from 'react'
import { genericMemo } from '../../../components/genericMemo'
import classes from './AudienceLanding.module.css'

const MAX_TEMPLATES = 4

const AUDIENCE_TEMPLATES = [
    {
        id: 'home-improvement',
        name: 'Loyal Home Improvement Shoppers',
        icon: IconHammer,
        color: 'sage',
        description:
            'Shoppers who frequently purchase home improvement products and supplies.',
        search: 'loyal home improvement shoppers',
    },
    {
        id: 'hilton-hotels',
        name: 'Hilton Hotels',
        icon: IconPlaneDeparture,
        color: 'coral',
        description: 'People that stay at Hilton Hotels often.',
        search: 'find travelers that stay at hilton hotels often',
    },
    {
        id: 'uber-eats-churn',
        name: 'Uber Eats Churn Risk',
        icon: IconTruckDelivery,
        color: 'plum',
        description: 'People who are about to churn from Uber Eats.',
        search: 'buyers about to churn from uber eats',
    },
    {
        id: 'meijer-shoppers',
        name: 'Frequent Meijer Shoppers',
        icon: IconShoppingCart,
        color: 'gold',
        description: 'Frequent shoppers at Meijer grocery and retail stores.',
        search: 'frequent buyers at meijer',
    },
    {
        id: 'eco',
        name: 'Eco-Conscious Cleaning Products',
        icon: IconLeaf,
        color: 'emerald',
        description:
            'Buyers of non-toxic, sustainable, or natural household cleaning products.',
        search: 'buyers of nontoxic or sustainable cleaning products',
    },
    {
        id: 'skin-care',
        name: 'Conquesting for L’Oreal Skin Care',
        icon: IconPlant2,
        color: 'magenta',
        description:
            'Buyers of competing brands of skin care products for a L’Oreal conquesting campaign.',
        search: "buyers of l'oreal skin care competitors",
    },
    {
        id: 'organic-food',
        name: 'Organic Food On A Budget',
        icon: IconSalad,
        color: 'sage',
        description:
            'Shoppers that want organically grown groceries and rely on store brands and private labels for the best value.',
        search: 'buyers of private label organic food',
    },
    {
        id: 'sparkling-water',
        name: 'Sparkling Water Shoppers',
        icon: IconBottleFilled,
        color: 'coral',
        description:
            'Purchase history of bottled and canned water products by type, for launching a new water brand.',
        search: 'buyers of bottled water and carbonated water with or without flavors',
    },
]

export interface AudienceLandingProps {
    onStartAudience: (description: string, title: string) => void
}

export const AudienceLanding: FC<AudienceLandingProps> = genericMemo(
    ({ onStartAudience }) => {
        const handleSelectTemplate = (templateId: string) => {
            const template = AUDIENCE_TEMPLATES.find((t) => t.id === templateId)
            if (template) {
                onStartAudience(template.search, template.name)
            }
        }

        return (
            <Stack gap='xl' mb='md'>
                <Box ta='center' mb='md'>
                    <ThemeIcon
                        size={80}
                        radius='xl'
                        variant='light'
                        color='blue'
                        mx='auto'
                        mb='md'
                    >
                        <IconUsers size={40} />
                    </ThemeIcon>
                    <Title order={1} size='h1' mb='xs'>
                        Create Your Audience
                    </Title>
                    <Text size='lg' c='dimmed' maw={600} mx='auto'>
                        Choose from a template to get started
                    </Text>
                </Box>

                {/* Template Cards */}
                <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing='lg'>
                    {AUDIENCE_TEMPLATES.slice(0, MAX_TEMPLATES).map(
                        (template) => {
                            const Icon = template.icon
                            return (
                                <Card
                                    key={template.id}
                                    component='button'
                                    type='button'
                                    shadow='sm'
                                    padding='lg'
                                    radius='md'
                                    withBorder
                                    className={`${classes.template} mantine-focus-auto`}
                                    onClick={() =>
                                        handleSelectTemplate(template.id)
                                    }
                                >
                                    <Stack gap='md' align='center' ta='center'>
                                        <ThemeIcon
                                            size={60}
                                            radius='xl'
                                            variant='light'
                                            color={template.color}
                                        >
                                            <Icon size={30} />
                                        </ThemeIcon>
                                        <Title order={4} size='h5'>
                                            {template.name}
                                        </Title>
                                        <Text
                                            size='sm'
                                            c='dimmed'
                                            lineClamp={3}
                                        >
                                            {template.description}
                                        </Text>
                                    </Stack>
                                </Card>
                            )
                        }
                    )}
                </SimpleGrid>

                <Box ta='center' mt='md'>
                    <Title order={1} size='h1' mb='xs'>
                        Don&apos;t see a template that fits?
                    </Title>
                    <Text size='lg' c='dimmed' maw={600} mx='auto'>
                        Use the text box below to create a custom audience
                        description.
                    </Text>
                </Box>
            </Stack>
        )
    }
)
